import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, Button, Typography } from '@mui/material';
import { toWordsOrdinal } from 'number-to-words';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import { DataTable } from 'src/components/common/table/DataTable';
import { PATIENT_LOADING_KEY } from 'src/features/patient';
import { IStudyBase, IStudyTableProps } from 'src/features/study/domain/types';
import { createStudy } from 'src/features/study/store/studyThunks';
import { RootState, AppDispatch } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';

export const StudyTable: React.FC<IStudyTableProps> = ({
	data,
	onStudyCreation,
}) => {
	const dispatch = useDispatch<AppDispatch>();

	const { patientId, siteSlug } = useParams();
	const navigate = useNavigate();
	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);

	const columns = [
		{
			key: 'study_id',
			label: 'Study ID',
			render: (study: IStudyBase) => study.patient_study_id,
		},
		{
			key: 'study_name',
			label: 'Study Name',
			render: (study: IStudyBase) => study.study_name,
		},
		{
			key: 'description',
			label: 'Description',
			render: (study: IStudyBase) => study.description,
		},
		{
			key: 'created_by',
			label: 'Created By',
			render: (study: IStudyBase) =>
				`${study.created_by.first_name} ${study.created_by.last_name}`,
		},
		{
			key: 'created_at',
			label: 'Created At',
			render: (study: IStudyBase) => formatTimestamp(study.created_at),
		},
		{
			key: 'action',
			label: 'Action',
			render: (study: IStudyBase) => (
				<Box
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						flexDirection: 'row',
						gap: 1,
					}}
				>
					<Button
						variant='outlined'
						size='small'
						onClick={() =>
							navigate(
								`/site/${siteSlug}/patient/${patientId}/study/${study.id}`,
							)
						}
						sx={{
							textTransform: 'none',
							borderColor: '#BE3327',
							color: '#BE3327',
							display: 'flex',
							justifyContent: 'center',
							borderRadius: '20px',
							'&:hover': {
								borderColor: '#BE3327',
								backgroundColor: '#BE332730',
							},
						}}
					>
						View
					</Button>
					{siteDetails?.ai_inference_enabled && (
						<Button
							variant='outlined'
							size='small'
							onClick={() =>
								navigate(
									`/site/${siteSlug}/patient/${patientId}/study/${study.id}/ai-report`,
								)
							}
							sx={{
								textTransform: 'none',
								borderColor: '#BE3327',
								color: '#BE3327',
								display: 'flex',
								justifyContent: 'center',
								borderRadius: '20px',
								whiteSpace: 'nowrap',
								'&:hover': {
									borderColor: '#BE3327',
									backgroundColor: '#BE332730',
								},
							}}
						>
							Generate AI Report
						</Button>
					)}
				</Box>
			),
		},
	];

	const handleStudyCreation = () => {
		if (!siteSlug || !patientId) return;

		const length = data.length;
		const visitNumber = length + 1;
		const name =
			toWordsOrdinal(visitNumber).replace(/^\w/, (c: string) =>
				c.toUpperCase(),
			) + ' Visit';

		const description =
			'Study for ' + name.toLowerCase() + ' of the patient';

		dispatch(
			createStudy({
				siteSlug,
				request: {
					study_name: name,
					description: description,
					patient_id: Number(patientId),
				},
				onSuccess: (response) => {
					snackbar('Study created successfully', 'success');
					onStudyCreation(response);
				},
			}),
		);
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 3,
				padding: 3,
				backgroundColor: 'white',
				borderRadius: '4px',
				marginTop: '20px',
				maxWidth: { xs: '100vw', sm: '100%' },
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					justifyContent: { xs: 'flex-start', sm: 'space-between' },
					alignItems: { xs: 'flex-start', sm: 'center' },
					gap: { xs: 2, sm: 0 },
					width: '100%',
				}}
			>
				<Typography
					variant='h6'
					sx={{
						fontSize: '20px',
						fontWeight: 500,
						color: 'rgba(0, 0, 0, 0.87)',
					}}
				>
					Studies
				</Typography>
				<Box sx={{ width: { xs: '100%', sm: '231px' } }}>
					{siteDetails?.can_add_patients && (
						<PrimaryButton
							color='error'
							onClick={handleStudyCreation}
							sx={{
								height: '36px',
								minHeight: '36px',
								'& .MuiButton-root': {
									height: '36px',
									minHeight: '36px',
								},
								backgroundColor: '#BE3327',

								'&:hover': {
									backgroundColor: '#A52D23',
								},
							}}
						>
							Create New Study
						</PrimaryButton>
					)}
				</Box>
			</Box>

			<DataTable
				columns={columns}
				data={data}
				loadingKey={PATIENT_LOADING_KEY}
				enablePagination={false}
				emptyMessage='No studies found'
			/>
		</Box>
	);
};
